import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { MicrosoftSsoService } from '../services/microsoft-sso.service';
import { LoginRequest } from '../models/auth.models';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  loginRequest: LoginRequest = {
    username: '',
    password: ''
  };
  
  loading = false;
  error: string | null = null;
  showForgotPassword = false;
  checkingEmail = false;
  ssoLoading = false;
  processingMicrosoftRedirect = false;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private microsoftSsoService: MicrosoftSsoService,
    private router: Router
  ) {}

  get showMicrosoftSignIn(): boolean {
    return this.microsoftSsoService.isSupportedEmail(this.loginRequest.username);
  }

  ngOnInit(): void {
    void this.completeMicrosoftRedirect();
  }

  onSubmit(): void {
    this.loading = true;
    this.error = null;
    this.showForgotPassword = false;
    
    this.authService.login(this.loginRequest).subscribe({
      next: () => {
        this.loading = false;
        void this.router.navigate(
          this.authService.hasCustomerContext() ? ['/'] : ['/customers']
        );
      },
      error: (error) => {
        const serverMsg = error && (error.error?.message || error.message || (error.status ? `Server returned ${error.status}` : null));
        this.error = serverMsg || 'Login failed. Please check your credentials.';
        this.loading = false;

        // Check if username has email after failed login
        if (this.loginRequest.username) {
          this.checkIfUsernameHasEmail(this.loginRequest.username);
        }
      }
    });
  }

  async signInWithMicrosoft(): Promise<void> {
    if (!this.showMicrosoftSignIn || this.ssoLoading) return;

    this.error = null;
    this.ssoLoading = true;

    try {
      await this.microsoftSsoService.signIn(this.loginRequest.username);
    } catch (error) {
      this.error = this.getMicrosoftErrorMessage(error);
      this.ssoLoading = false;
    }
  }

  private checkIfUsernameHasEmail(username: string): void {
    this.checkingEmail = true;
    
    this.userService.checkUsernameHasEmail(username).subscribe({
      next: (response) => {
        this.checkingEmail = false;
        this.showForgotPassword = response.hasEmail;
      },
      error: () => {
        this.checkingEmail = false;
        this.showForgotPassword = false;
      }
    });
  }

  goToForgotPassword(): void {
    // Navigate to forgot password page
    this.router.navigate(['/forgot-password'], { 
      queryParams: { username: this.loginRequest.username } 
    });
  }

  private async completeMicrosoftRedirect(): Promise<void> {
    this.processingMicrosoftRedirect = true;

    try {
      const microsoftIdToken =
        await this.microsoftSsoService.handleRedirectAndGetIdToken();

      if (!microsoftIdToken) return;

      this.ssoLoading = true;
      await firstValueFrom(this.authService.exchangeMicrosoftToken(microsoftIdToken));
      await this.router.navigate(['/customers']);
    } catch (error) {
      this.error = this.getMicrosoftErrorMessage(error);
      this.ssoLoading = false;
    } finally {
      this.processingMicrosoftRedirect = false;
    }
  }

  private getMicrosoftErrorMessage(error: any): string {
    if (error?.status === 403) {
      return 'This Microsoft account is not authorized to access DSDN.';
    }

    return error?.error?.detail
      || error?.error?.message
      || 'Microsoft sign-in could not be completed. Please try again.';
  }
}
